import { useEffect, useRef, useState, useCallback } from 'react'
import * as d3 from 'd3'
import Popup from './PopupMessage'

const COLORS = {
    background:'#FFFDF8',
    panel:'#F7F1E3',
    root:'#6B4E16',
    branch:'#8A6A22',
    leaf:'#4A8B5C',
    deceased:'#9A7B2E',
    link:'#B8A16D',
    selected:'#B84040',
    gold:'#C9A03C',
    text:'#3B3020',
    muted:'#837255',
    white:'#FFF8E8'
}

const getNodeFill = d => {
    if(d.depth === 0) return COLORS.root
    if(d.data.isAlive === false) return COLORS.deceased
    return d.children || d._children ? COLORS.branch : COLORS.leaf
}

const getFullName = d => {
    const names = []
    let cursor = d
    let guard = 0

    while(cursor && guard < 7) {
        names.push(cursor.data.firstName)
        cursor = cursor.parent
        guard += 1
    }

    return names.join(' ')
}

const truncateName = value => {
    if(!value) return ''
    return value.length > 18 ? `${value.slice(0, 18)}...` : value
}

const CollapseTree = ({ familyData, selectedMemberId }) => {
    const wrapperRef = useRef(null)
    const [collapsedIds, setCollapsedIds] = useState(new Set())
    const [treeError, setTreeError] = useState('')

    const toggleNode = useCallback(id => {
        setCollapsedIds(prev => {
            const next = new Set(prev)
            if(next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }, [])

    useEffect(() => {
        if(!familyData || familyData.length === 0) return

        try {
            const structure = d3.stratify().id(d => d._id).parentId(d => d.parentId)(familyData)
            const initial = new Set()
            structure.each(node => {
                if(node.depth >= 2 && node.children && node.data._id !== selectedMemberId) {
                    initial.add(node.data._id)
                }
            })
            setCollapsedIds(initial)
        } catch (error) {
            setTreeError(error.message)
        }
    }, [familyData, selectedMemberId])

    useEffect(() => {
        if(!wrapperRef.current || !familyData || familyData.length === 0) return

        try {
            setTreeError('')
            wrapperRef.current.innerHTML = ''

            const rect = wrapperRef.current.getBoundingClientRect()
            const containerW = rect.width || 1200
            const containerH = rect.height || 700
            const margin = { top: 80, right: 220, bottom: 80, left: 220 }
            const cardWidth = 240
            const cardHeight = 62
            const cardRadius = 9
            const toggleX = cardWidth / 2 - 24
            const textX = toggleX - 36
            const nodeGapY = 104
            const nodeGapX = 300

            const root = d3.stratify().id(d => d._id).parentId(d => d.parentId)(familyData)
            const selectedNode = selectedMemberId ? root.descendants().find(d => d.data._id === selectedMemberId) : null
            const selectedLineage = new Set()
            let selectedCursor = selectedNode

            while(selectedCursor) {
                selectedLineage.add(selectedCursor.data._id)
                selectedCursor = selectedCursor.parent
            }

            root.each(node => {
                if(node.children) node._children = node.children
                const shouldCollapse = collapsedIds.has(node.data._id) && !selectedLineage.has(node.data._id)
                if(shouldCollapse) node.children = null
            })

            const visibleNodes = root.descendants()
            const visibleLinks = root.links()
            const maxDepth = d3.max(visibleNodes, d => d.depth) || 1
            const width = Math.max(containerW, margin.left + margin.right + maxDepth * nodeGapX)
            const height = Math.max(containerH, margin.top + margin.bottom + visibleNodes.length * nodeGapY)

            d3.tree()
                .nodeSize([nodeGapY, nodeGapX])
                .separation((a, b) => a.parent === b.parent ? 1.1 : 1.5)(root)

            const minX = d3.min(visibleNodes, d => d.x) || 0
            const maxX = d3.max(visibleNodes, d => d.x) || 0
            const offsetY = Math.max(margin.top, (height - (maxX - minX)) / 2)

            const svg = d3.select(wrapperRef.current)
                .append('svg')
                .attr('width', containerW)
                .attr('height', containerH)
                .attr('encoding', 'UTF-8')
                .style('touch-action', 'none')
                .style('cursor', 'grab')

            const defs = svg.append('defs')
            const shadow = defs.append('filter')
                .attr('id', 'collapseTreeShadow')
                .attr('x', '-35%')
                .attr('y', '-35%')
                .attr('width', '170%')
                .attr('height', '170%')

            shadow.append('feDropShadow')
                .attr('dx', 0)
                .attr('dy', 5)
                .attr('stdDeviation', 4)
                .attr('flood-color', '#4F3A13')
                .attr('flood-opacity', 0.16)

            const zoomLayer = svg.append('g')
            const g = zoomLayer.append('g')
                .attr('transform', `translate(${width - margin.right},${offsetY - minX})`)

            const linkPath = d => {
                const sourceX = -d.source.y
                const sourceY = d.source.x
                const targetX = -d.target.y
                const targetY = d.target.x
                const midX = (sourceX + targetX) / 2
                return `M${sourceX},${sourceY}C${midX},${sourceY} ${midX},${targetY} ${targetX},${targetY}`
            }

            g.selectAll('.collapse-tree__link')
                .data(visibleLinks, d => d.target.data._id)
                .enter()
                .append('path')
                .attr('class', 'collapse-tree__link')
                .attr('fill', 'none')
                .attr('stroke', d => selectedLineage.has(d.target.data._id) ? COLORS.selected : COLORS.link)
                .attr('stroke-width', d => selectedLineage.has(d.target.data._id) ? 3.5 : 2)
                .attr('stroke-opacity', d => selectedLineage.has(d.target.data._id) ? 0.9 : 0.46)
                .attr('d', linkPath)

            const node = g.selectAll('.collapse-tree__node')
                .data(visibleNodes, d => d.data._id)
                .enter()
                .append('g')
                .attr('class', d => `collapse-tree__node${selectedMemberId === d.data._id ? ' collapse-tree__node--selected' : ''}`)
                .attr('transform', d => `translate(${-d.y},${d.x})`)

            node.append('rect')
                .attr('class', 'collapse-tree__card')
                .attr('x', -cardWidth / 2)
                .attr('y', -cardHeight / 2)
                .attr('width', cardWidth)
                .attr('height', cardHeight)
                .attr('rx', cardRadius)
                .attr('fill', d => selectedMemberId === d.data._id ? '#FFF3CC' : COLORS.panel)
                .attr('stroke', d => selectedLineage.has(d.data._id) ? COLORS.gold : 'rgba(184, 161, 109, 0.45)')
                .attr('stroke-width', d => selectedMemberId === d.data._id ? 3 : 1.4)
                .attr('filter', 'url(#collapseTreeShadow)')
                .style('cursor', 'default')

            node.append('line')
                .attr('class', 'collapse-tree__divider')
                .attr('x1', toggleX - 24)
                .attr('x2', toggleX - 24)
                .attr('y1', -cardHeight / 2 + 10)
                .attr('y2', cardHeight / 2 - 10)
                .attr('stroke', 'rgba(184, 161, 109, 0.36)')
                .attr('stroke-width', 1)

            node.append('circle')
                .attr('class', 'collapse-tree__toggle')
                .attr('cx', toggleX)
                .attr('cy', 0)
                .attr('r', 18)
                .attr('fill', getNodeFill)
                .attr('stroke', COLORS.white)
                .attr('stroke-width', 2)
                .style('cursor', d => d._children ? 'pointer' : 'default')
                .on('click', (event, d) => {
                    event.stopPropagation()
                    if(d._children) toggleNode(d.data._id)
                })

            node.append('text')
                .attr('class', 'collapse-tree__toggle-icon')
                .attr('x', toggleX)
                .attr('y', 1)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('fill', COLORS.white)
                .style('pointer-events', 'none')
                .text(d => d._children ? (d.children ? '-' : '+') : '')

            node.append('text')
                .attr('class', 'collapse-tree__name')
                .attr('x', textX)
                .attr('y', -7)
                .attr('text-anchor', 'end')
                .attr('dominant-baseline', 'middle')
                .attr('fill', d => d.data.isAlive === false ? COLORS.muted : COLORS.text)
                .style('cursor', 'default')
                .text(d => truncateName(d.data.firstName))

            node.append('text')
                .attr('class', 'collapse-tree__meta')
                .attr('x', textX)
                .attr('y', 17)
                .attr('text-anchor', 'end')
                .attr('fill', COLORS.muted)
                .text(d => d._children ? `${d._children.length} فرع` : 'نهاية الفرع')

            node.append('title').text(getFullName)

            const zoom = d3.zoom()
                .scaleExtent([0.18, 2.4])
                .on('zoom', event => {
                    zoomLayer.attr('transform', event.transform)
                    svg.style('cursor', 'grabbing')
                })
                .on('end', () => svg.style('cursor', 'grab'))

            svg.call(zoom)

            const rootSceneX = width - margin.right
            const rootSceneY = offsetY
            const startScale = 0.92
            const startTransform = d3.zoomIdentity
                .translate(
                    containerW - margin.right - rootSceneX * startScale,
                    containerH / 2 - rootSceneY * startScale
                )
                .scale(startScale)

            svg.call(zoom.transform, startTransform)

            if(selectedNode) {
                const visibleSelectedNode = visibleNodes.find(d => d.data._id === selectedMemberId)
                if(visibleSelectedNode) {
                    const focusScale = 1.18
                    const x = (width - margin.right) - visibleSelectedNode.y
                    const y = (offsetY - minX) + visibleSelectedNode.x
                    svg.transition()
                        .duration(650)
                        .call(
                            zoom.transform,
                            d3.zoomIdentity
                                .translate(containerW / 2 - x * focusScale, containerH / 2 - y * focusScale)
                                .scale(focusScale)
                        )
                }
            }
        } catch (error) {
            setTreeError(error.message)
        }
    }, [familyData, collapsedIds, selectedMemberId, toggleNode])

    return (
        <>
            <div className='tree__wrapper tree__wrapper--collapse' ref={wrapperRef}></div>
            {treeError && <Popup danger><p>{treeError}</p></Popup>}
        </>
    )
}

export default CollapseTree
